/**
 * Store des options
 */
import { defineStore } from "pinia";
import OptionService from "../services/OptionService";
import { useUsersStore } from "../store/users.store";
import { IOption } from "../models/options";
import { IOptionForm } from "../models/forms/optionForm";
import { IOptionFilter } from "../models/filters/optionFilter";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { FrontUser } from "../models/user";
import { ICenter } from "../models/center";
import { useStartupStore } from "./startup.store";

export const useOptions = defineStore("options", {
    state: () => ({
        datas: [] as Array<IOption>,
        current: {} as IOption,
        itemForm: {} as IOptionForm
    }),
    getters: {
        /**
         * Récupère une option par son identifiant dans le store, sans passer par l'api
         */
        getOptionById: (state) => (id: number): IOption | undefined => {
            return state.datas.find((item) => item.id == id);
        },

        /**
         * Index des options par code — reconstruit une seule fois quand datas change (caché par Pinia)
         */
        optionsByCode(state): Map<string, IOption[]> {
            const map = new Map<string, IOption[]>();
            for (const item of state.datas) {
                const arr = map.get(item.code) ?? [];
                arr.push(item);
                map.set(item.code, arr);
            }
            return map;
        },

        /**
         * Récupère une option par son code dans le store, sans passer par l'api.
         * Utilise optionsByCode (O(1)) puis filtre les candidats par priorité.
         */
        getOptionByCode(): (code: string, user: FrontUser, center: ICenter) => IOption | undefined {
            return (code: string, user: FrontUser, center: ICenter): IOption | undefined => {
                const candidates: IOption[] = this.optionsByCode.get(code) ?? [];
                return candidates.find(item => item.user == user?.email)
                    ?? candidates.find(item => item.center?.id == center?.id)
                    ?? candidates.find(item => item.society?.id == center?.society?.id)
                    ?? candidates.find(item => item.country?.id == center?.country?.id)
                    ?? candidates.find(item => item.species?.id == center?.species?.id)
                    ?? candidates.find(item => item.default);
            };
        },

        /**
         * Récupère une option par son code et son centre attribué dans le store, sans passer par l'api
         */
        getOptionByCodeForCenter: (state) => (code: string): IOption | undefined => {

            const userStore = useUsersStore();

            if (!userStore.currentCenter?.id) return undefined;

            return state.datas.find((item) => item.code == code && item.center?.id == userStore.currentCenter?.id)
        },

        /**
         * Récupère une option par son code et son centre attribué dans le store, sans passer par l'api
         */
        getOptionBooleanByCode: (state) => (code: string): boolean => {

            const userStore = useUsersStore();
            const useOptionsActions = useOptions();

            let valueFound = useOptionsActions.getOptionByCode(code, userStore.currentUser, userStore.currentCenter)

            let valueBoolean = valueFound?.value

            if(valueBoolean) {
                return (valueBoolean == 'true')
            } else {
                return false
            }
        },


        /**
         * Récupère une option par son code et son utilisateur attribué dans le store, sans passer par l'api
         */
        getOptionByCodeForUser: (state) => (code: string, user: string): IOption | undefined => {
            return state.datas.find((item) => item.code == code && item.user == user)
        }
    },
    actions: {
        /**
         * Chargement des données dans le store
         * @param filter Filtre à appliquer
         */
        async load(filter: IOptionFilter) {
            const startupStore = useStartupStore();
            if(startupStore.isMainInitializing) {
                let result = await OptionService.getAll<IOptionFilter, IOption>(filter);
                this.datas = [];
                if(!result.error) {
                    this.datas = result.data as IOption[];
                }
                return result;
            }
            else {
                console.error("Store loading aborted : triggered from outside main.ts context");
            }
        },

        /**
         * Récupération d'une option par son identifiant
         * @param id Id de l'option
         * @returns
         */
        async get(id: number) {
            let result = await OptionService.getById<IOption>(id);
            // mettre à jour dans le store ?
            if ( !result.error) {
                let index = this.datas.findIndex((a) => a.id == id);
                if (index >= 0)
                    this.datas[index] = result.data as IOption;
                // on le stock dans le current puis dans le form
                this.current = result.data as IOption;
                this.itemForm = await this.toForm(result.data as IOption);
            }
            return result;
        },

        /**
        * Récupération de l'objet dans le store
        * @param id Identifiant de l'objet
        * @returns
        */
        async searchStoreById (id: number) {
            this.current = this.datas.find((objet) => objet.id == id)!;
            this.itemForm = await this.toForm(this.current as IOption);
            return this.current;
        },

        /**
         * Met à jour l'option sur le serveur
         */
        async save(maj:boolean = true, isPatch:boolean = false)
        {
            console.log("Sauvegarde de l'option "+this.itemForm.code+" pour l'utilisateur "+this.itemForm.user +" (id="+this.itemForm.id+")");
            let result = {} as ServiceOperationResult<IOption | IDataOperationResult<IOption>>;
            if ( this.itemForm.id > 0) {
                result = await OptionService.update(this.itemForm);

                if ( !result.error)
                {
                    this.current = result.data as IOption;
                    if ( maj )
                    {
                        let index = this.datas.findIndex((a) => a.id == this.itemForm.id);
                        if (index >= 0) {
                            this.datas[index] = this.current;
                        } else {
                            const error = new DataOperationError();
                            error.REQUEST_ERROR = "Item non trouvé dans le store";
                            return new ServiceOperationResult<IDataOperationResult<IOption>>(true, {errors: error});
                        }
                    }
                }
            } else {
                result = await OptionService.create(this.itemForm);
                if ( !result.error)
                {
                    this.current = result.data as IOption;
                    this.datas.push(this.current);
                }
            }
            return result;
        },

       async saveSpecificForm(form: IOptionForm, maj: boolean = true): Promise<ServiceOperationResult<IOption | IDataOperationResult<IOption>>> {
        let result = {} as ServiceOperationResult<IOption | IDataOperationResult<IOption>>;  
        
        if (form.id > 0) {
            result = await OptionService.update(form);
            
            if (!result.error && maj) {
                const savedOption = result.data as IOption;
                let index = this.datas.findIndex((a) => a.id == form.id);
                if (index >= 0) {
                    this.datas[index] = savedOption;
                } else {
                    const error = new DataOperationError();
                    error.REQUEST_ERRROR = "Item non trouvé dans le store";
                    return new ServiceOperationResult<IDataOperationResult<IOption>>(true, {errors: error});
                }
            }
        } else {
            result = await OptionService.create(form);
            if (!result.error) {
                const newOption = result.data as IOption;
                this.datas.push(newOption);
            }
        }
        
        return result;
    },

    /**
     * Sauvegarde multiple d'options en parallèle
     * @param optionsToSave Tableau d'objets {option: IOption, value: string}
     */
    async batchSaveOptions(optionsToSave: Array<{ option: IOption, value: string }>): Promise<ServiceOperationResult<IOption | IDataOperationResult<IOption>>[]> {
        // Sauvegardes séquentielles : toForm() écrit sur this.itemForm (état partagé),
        // donc des appels parallèles via Promise.all corrompent le store local
        // (form.id écrasé → datas mis à jour au mauvais index → options mélangées)
        const results: ServiceOperationResult<IOption | IDataOperationResult<IOption>>[] = [];
        for (const { option, value } of optionsToSave) {
            try {
                const form = await this.toForm(option);
                form.value = value;
                results.push(await this.saveSpecificForm(form));
            } catch (error) {
                console.error('Erreur lors de la sauvegarde de l\'option:', option.code, error);
                results.push(new ServiceOperationResult<IDataOperationResult<IOption>>(true, { errors: error }));
            }
        }
        return results;
    },

    /**
     * Sauvegarde multiple d'options avec gestion d'erreurs avancée
     * @param optionsData Tableau d'objets {code: string, value: string, user?: FrontUser, center?: ICenter}
     */
    async batchSaveOptionsByCode(optionsData: Array<{ code: string, value: string, user?: FrontUser, center?: ICenter }>): Promise<{ successes: IOption[], errors: Array<{ code: string, error: any }> }> {
        const results = { successes: [] as IOption[], errors: [] as Array<{ code: string, error: any }> };

        // Sauvegardes séquentielles pour éviter la corruption du store via this.itemForm partagé
        for (const { code, value, user, center } of optionsData) {
            try {
                const option = user && center
                    ? this.getOptionByCode(code, user, center)
                    : this.getOptionByCodeForCenter(code);

                if (!option) {
                    results.errors.push({ code, error: `Option ${code} non trouvée` });
                    continue;
                }

                const form = await this.toForm(option);
                form.value = value;
                const result = await this.saveSpecificForm(form);

                if (!result.error) {
                    results.successes.push(result.data as IOption);
                } else {
                    results.errors.push({ code, error: result.error });
                }
            } catch (error) {
                results.errors.push({ code, error });
            }
        }

        return results;
    },

        async toForm(item: IOption): Promise<IOptionForm> {

            Object.assign(this.itemForm, item);
            this.itemForm.id = item.id;
            this.itemForm.centerId = item.center?.id;
            this.itemForm.societyId = item.society?.id;
            this.itemForm.countryId = item.country?.id;
            this.itemForm.speciesId = item.species?.id;
            return this.itemForm;
        },

        /**
         * Initialise un objet vide pour le formulaire
         */
        newForm(): IOptionForm {
            this.itemForm = {
                id: 0,
                user: undefined,
                center: undefined,
                society: undefined,
                country: undefined,
                species: undefined,
                default: true,
                startDate: undefined,
                endDate: undefined,
                description: undefined,
                value: undefined,
                code: undefined
            } as IOptionForm;
            return this.itemForm;
        },

        /**
         * Initialise un objet vide pour les referentiels
         */
        newItem(): IOption {
            this.current = {
                id: 0,
                user: undefined,
                center: undefined,
                society: undefined,
                country: undefined,
                species: undefined,
                default: true,
                startDate: undefined,
                endDate: undefined,
                description: undefined,
                value: "",
                code: ""
            } as IOption;

            return this.current;
        }
    },
});
